/**
 * Drone-mission progress stepper (#374) — a compact at-a-glance "where is my request" track for a
 * requestor: five dots (Requested · Accepted · Confirmed · Enroute · Flown, mirroring the detail
 * panel lifecycle) filled up to the furthest milestone reached. Derived purely from the shared
 * state machine via `missionProgressIndex`, so it
 * can never show a step the lifecycle doesn't support. The CURRENT milestone name rides the title
 * + aria-label (not visible text) so it never duplicates the status badge the row already shows;
 * the dots carry the progress. A terminal off-ramp (declined / cancelled) has no progress index —
 * the caller's status badge carries it, so this renders nothing (returns null) rather than a
 * misleading empty track.
 */
import type { MissionStatus } from '@tsr/shared-types';
import { MISSION_PROGRESS_STEPS, missionProgressIndex } from './droneMissionUi';

export function MissionProgress({ status }: { status: MissionStatus }): JSX.Element | null {
  const idx = missionProgressIndex(status);
  if (idx == null) return null; // declined / cancelled — the status badge tells the story
  const label = `${MISSION_PROGRESS_STEPS[idx]} — step ${idx + 1} of ${MISSION_PROGRESS_STEPS.length}`;
  return (
    <div
      className="mt-1 flex items-center gap-1"
      data-testid="mission-progress"
      data-step={MISSION_PROGRESS_STEPS[idx]}
      role="img"
      aria-label={label}
      title={label}
    >
      {MISSION_PROGRESS_STEPS.map((step, i) => (
        <span
          key={step}
          aria-hidden="true"
          className={`h-1.5 w-1.5 rounded-full ${i <= idx ? 'bg-cyan-400' : 'bg-slate-600'}`}
        />
      ))}
    </div>
  );
}
